import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ArrowLeft, ArrowRight, Check, Loader2, Lock, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  { id: "basic", name: "Basic", price: 300, note: "2–3 pages" },
  { id: "standard", name: "Standard", price: 450, note: "Up to 5 pages" },
  { id: "premium", name: "Premium", price: 650, note: "Up to 7 pages" },
];

const MONTHLY = [
  { id: "hosting", name: "Keep included hosting", price: 15, note: "$15/month total" },
  { id: "essential", name: "Essential Care", price: 50, note: "$50/month total, including hosting" },
  { id: "premium", name: "Premium Care", price: 100, note: "$100/month total, including hosting" },
];

const optionCls = (active) =>
  `flex w-full items-center justify-between gap-4 rounded-2xl border p-4.5 text-left transition-all ${
    active
      ? "border-blue-400 bg-blue-500/10 shadow-lg shadow-blue-600/10"
      : "border-white/10 bg-white/5 hover:border-white/25"
  }`;

export default function PackageSelector({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [pkg, setPkg] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const selPkg = PACKAGES.find((p) => p.id === pkg);
  const selMo = MONTHLY.find((m) => m.id === monthly);
  const billingDate = new Date(Date.now() + 30 * 86400000).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        package: pkg,
        monthly,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Couldn't start checkout. Please try again or call (507) 841-1801.");
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      data-testid="package-selector"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0B1026] p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between">
          <p className="font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-400">
            Step {step} of 3
          </p>
          <button
            onClick={onClose}
            data-testid="selector-close-btn"
            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {step === 1 && (
          <div>
            <h2 className="mt-3 font-display text-2xl font-bold text-white">Choose your website package</h2>
            <div className="mt-5 space-y-3">
              {PACKAGES.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPkg(p.id)}
                  data-testid={`selector-package-${p.id}`}
                  className={optionCls(pkg === p.id)}
                >
                  <span className="flex items-center gap-3">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                        pkg === p.id ? "border-blue-400 bg-blue-600" : "border-white/25"
                      }`}
                    >
                      {pkg === p.id && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-white">{p.name}</span>
                      <span className="block text-xs text-slate-400">{p.note}</span>
                    </span>
                  </span>
                  <span className="font-display text-lg font-bold text-white">${p.price}
                    <span className="ml-1 text-xs font-medium text-slate-400">one-time</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="mt-3 font-display text-2xl font-bold text-white">Choose your ongoing support</h2>
            <div className="mt-5 space-y-3">
              {MONTHLY.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMonthly(m.id)}
                  data-testid={`selector-monthly-${m.id}`}
                  className={optionCls(monthly === m.id)}
                >
                  <span className="flex items-center gap-3">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                        monthly === m.id ? "border-blue-400 bg-blue-600" : "border-white/25"
                      }`}
                    >
                      {monthly === m.id && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-white">{m.name}</span>
                      <span className="block text-xs text-slate-400">{m.note}</span>
                    </span>
                  </span>
                  <span className="font-display text-lg font-bold text-white">${m.price}
                    <span className="ml-1 text-xs font-medium text-slate-400">/mo</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && selPkg && selMo && (
          <div>
            <h2 className="mt-3 font-display text-2xl font-bold text-white">Your order summary</h2>
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="selector-summary">
              <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-4">
                <div>
                  <p className="text-sm font-semibold text-white">{selPkg.name} Website Package</p>
                  <p className="mt-0.5 text-xs text-slate-400">{selPkg.note} · one-time payment</p>
                </div>
                <p className="font-display text-lg font-bold text-white">${selPkg.price}</p>
              </div>
              <div className="flex items-start justify-between gap-4 border-b border-white/10 py-4">
                <div>
                  <p className="text-sm font-semibold text-white">{selMo.name}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
                    <CalendarClock className="h-3 w-3" /> First charge {billingDate} (30 days after purchase)
                  </p>
                </div>
                <p className="font-display text-lg font-bold text-white">${selMo.price}/mo</p>
              </div>
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm font-semibold text-slate-300">Due today</p>
                <p className="font-display text-2xl font-bold text-blue-400" data-testid="selector-total">
                  ${selPkg.price}
                </p>
              </div>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              Your monthly total is ${selMo.price}/month — that already includes the $15 hosting. Recurring billing
              begins {billingDate}.
            </p>
          </div>
        )}

        <div className="mt-7 flex items-center justify-between gap-3">
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              data-testid="selector-back-btn"
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-300 transition-colors hover:border-white/30 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : (
            <span />
          )}
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !pkg) || (step === 2 && !monthly)}
              data-testid="selector-next-btn"
              className="flex items-center gap-1.5 rounded-full bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-40"
            >
              Next <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={checkout}
              disabled={loading}
              data-testid="selector-checkout-btn"
              className="flex items-center gap-2 rounded-full bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {loading ? "Opening checkout…" : "Continue to Checkout"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
